/**
 * Free-text token input with existing-tag autocomplete. A tag comes into existence by
 * being typed here — that is what makes it ambient rather than curated.
 */
import { useId, useState } from "react";

export function TagInput({
  value,
  suggestions,
  onChange,
}: {
  value: readonly string[];
  suggestions: readonly string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const listId = useId();
  return (
    <div className="flex flex-wrap items-center gap-1 border border-hair px-2 py-1">
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 bg-hair-soft px-2 py-0.5 font-mono text-[10.5px] uppercase">
          {tag}
          <button type="button" aria-label={`remove ${tag}`} onClick={() => onChange(value.filter((one) => one !== tag))}>
            ×
          </button>
        </span>
      ))}
      <input
        aria-label="add a tag"
        list={listId}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            const tag = draft.trim();
            // A tag is born by being typed: an unknown name is created server-side.
            if (tag !== "" && !value.some((one) => one.toLowerCase() === tag.toLowerCase())) {
              onChange([...value, tag]);
            }
            setDraft("");
          } else if (event.key === "Backspace" && draft === "" && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        className="min-w-24 flex-1 bg-transparent font-mono text-[11.5px] focus:outline-none"
      />
      <datalist id={listId}>
        {suggestions
          .filter((one) => one.toLowerCase().includes(draft.toLowerCase()))
          .map((one) => (
            // Text content as well as `value`: an <option> with only a value attribute
            // has no accessible name, so it is unreachable by role and name — invisible
            // to a screen reader and to any test that asks for it the way a person would.
            <option key={one} value={one}>
              {one}
            </option>
          ))}
      </datalist>
    </div>
  );
}
